import { PayloadAction } from '@reduxjs/toolkit';

import { ConditionAddress, ConditionDisplayOption, ConditionOption, DesignState } from 'editor/src/store/design/types';

import getConditionChildrenKey from './getConditionChildrenKey';
import releaseConditionOptionChildren from './releaseConditionOptionChildren';

const importConditionReducer = (
  state: DesignState,
  action: PayloadAction<{
    address: ConditionAddress;
    name: string;
    description?: string;
    displayOption: ConditionDisplayOption;
    showOnAllSpreads?: true;
    /** Freshly generated options — never the ids from the imported file. */
    options: ConditionOption[];
    activeOptionId: string;
  }>,
) => {
  const { address, name, description, displayOption, showOnAllSpreads, options, activeOptionId } = action.payload;
  const { spreadIndex, conditionId } = address;
  const conditionGroup = state.designData?.spreads[spreadIndex]?.conditionGroup;
  if (!conditionGroup) {
    return;
  }

  const condition = conditionGroup.conditions[conditionId];
  if (!condition) {
    return;
  }

  const oldOptionIds = condition.options.map((option) => option.id);

  // The whole option set is being replaced at once, so every old option is
  // released — same per-option cleanup as removeConditionOptionReducer, but
  // without its ">= 2 options left" guard, which does not apply here.
  oldOptionIds.forEach((optionId) => {
    releaseConditionOptionChildren(state, conditionGroup, spreadIndex, conditionId, optionId);
  });

  // A text following this condition by its OWN authored values (not
  // useOptionLabel) keyed those values by the old option ids, which no longer
  // exist — it has nothing left to show for any option, so it stops following.
  // A useOptionLabel text keeps following: it never referenced option ids, it
  // just shows whichever option is active, and the operation re-applies the
  // active option after this so it picks up the new label immediately.
  state.designData?.spreads.forEach((spread) => {
    spread.pages[0]?.groups.media?.forEach((element) => {
      if (
        element.type === 'text' &&
        element.extra.textVariants?.conditionId === conditionId &&
        !element.extra.textVariants.useOptionLabel
      ) {
        element.extra.textVariants = undefined;
      }
    });
  });

  condition.name = name;
  condition.description = description;
  condition.displayOption = displayOption;
  condition.showOnAllSpreads = showOnAllSpreads;
  condition.options = options;
  condition.activeOptionId = activeOptionId;

  options.forEach((option) => {
    conditionGroup.children[getConditionChildrenKey(conditionId, option.id)] = [];
  });
};

export default importConditionReducer;
